import { supabase } from '../lib/supabase';
import sharepointService from './sharepointService';
import auditLogService from './auditLogService';
import microsoftGraphService from './microsoftGraphService';

// Storage strategy configuration
let storageStrategy = 'supabase'; // Default: 'supabase' or 'sharepoint'

export const documentService = {
  // Storage configuration
  setStorageStrategy(strategy) {
    if (strategy !== 'supabase' && strategy !== 'sharepoint') {
      throw new Error('Invalid storage strategy. Must be "supabase" or "sharepoint"');
    }
    storageStrategy = strategy;
    console.log(`[STORAGE] Strategy set to: ${strategy}`);
    
    // Persist strategy to localStorage for page reload persistence
    try {
      localStorage.setItem('zentri_storage_strategy', strategy);
    } catch (err) {
      console.warn('[STORAGE] Could not persist strategy to localStorage:', err);
    }
  },

  getStorageStrategy() {
    // Check localStorage first for persisted strategy
    try {
      const persisted = localStorage.getItem('zentri_storage_strategy');
      if (persisted && (persisted === 'supabase' || persisted === 'sharepoint')) {
        storageStrategy = persisted;
      }
    } catch (err) {
      console.warn('[STORAGE] Could not read strategy from localStorage:', err);
    }
    
    return storageStrategy;
  },

  // Document Bases
  async getDocumentBases() {
    try {
      const { data, error } = await supabase?.from('document_bases')?.select(`
          *,
          owner:user_profiles!owner_id(full_name, email),
          _count:documents(count)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(base => ({
        id: base?.id,
        name: base?.name,
        description: base?.description,
        ownerId: base?.owner_id,
        ownerName: base?.owner?.full_name,
        isPublic: base?.is_public,
        createdAt: base?.created_at,
        updatedAt: base?.updated_at,
        documentCount: base?._count?.[0]?.count || 0
      }));
    } catch (error) {
      console.error('Error loading document bases:', error);
      throw error;
    }
  },

  async createDocumentBase(name, description, isPublic = false) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('document_bases')?.insert({
          name: name?.trim(),
          description: description?.trim() || null,
          owner_id: user?.id,
          is_public: isPublic
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        ownerId: data?.owner_id,
        isPublic: data?.is_public,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating document base:', error);
      throw error;
    }
  },

  async updateDocumentBase(id, updates) {
    try {
      const { data, error } = await supabase?.from('document_bases')?.update({
          name: updates?.name?.trim(),
          description: updates?.description?.trim() || null,
          is_public: updates?.isPublic
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        ownerId: data?.owner_id,
        isPublic: data?.is_public,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating document base:', error);
      throw error;
    }
  },

  async deleteDocumentBase(id) {
    try {
      const { error } = await supabase?.from('document_bases')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting document base:', error);
      throw error;
    }
  },

  // Enhanced delete with validation
  async deleteDocumentBaseWithValidation(id) {
    try {
      // Get document base details first
      const { data: docBase, error: fetchError } = await supabase
        ?.from('document_bases')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (fetchError || !docBase) {
        throw new Error('Document base not found');
      }

      // Check permissions
      const permission = await this.canManageItem('document_base', id, docBase?.owner_id);
      if (!permission?.canManage) {
        throw new Error(permission?.reason || 'Access denied');
      }

      // Check if document base is empty
      const { data: documents } = await supabase
        ?.from('documents')
        ?.select('id')
        ?.eq('document_base_id', id)
        ?.eq('status', 'active');

      const { data: folders } = await supabase
        ?.from('folders')
        ?.select('id')
        ?.eq('document_base_id', id);

      if (documents?.length > 0 || folders?.length > 0) {
        throw new Error(`Cannot delete non-empty document base. Contains ${documents?.length || 0} documents and ${folders?.length || 0} folders.`);
      }

      // Proceed with deletion
      const { error } = await supabase?.from('document_bases')?.delete()?.eq('id', id);
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting document base:', error);
      throw error;
    }
  },

  // Enhanced rename with validation
  async renameDocumentBaseWithValidation(id, newName) {
    try {
      if (!newName?.trim()) {
        throw new Error('Name cannot be empty');
      }

      // Get document base details first
      const { data: docBase, error: fetchError } = await supabase
        ?.from('document_bases')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (fetchError || !docBase) {
        throw new Error('Document base not found');
      }

      // Check permissions
      const permission = await this.canManageItem('document_base', id, docBase?.owner_id);
      if (!permission?.canManage) {
        throw new Error(permission?.reason || 'Access denied');
      }

      // Check for duplicate names
      const { data: existing } = await supabase
        ?.from('document_bases')
        ?.select('id')
        ?.eq('name', newName?.trim())
        ?.neq('id', id);

      if (existing?.length > 0) {
        throw new Error('A document base with this name already exists');
      }

      // Update the name
      const { data, error } = await supabase
        ?.from('document_bases')
        ?.update({ name: newName?.trim() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        ownerId: data?.owner_id,
        isPublic: data?.is_public,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error renaming document base:', error);
      throw error;
    }
  },

  // Folders
  async getFolders(documentBaseId) {
    try {
      const { data, error } = await supabase?.from('folders')?.select(`
          *,
          owner:user_profiles!owner_id(full_name),
          parent:folders!parent_folder_id(name)
        `)?.eq('document_base_id', documentBaseId)?.order('name');

      if (error) throw error;

      return data?.map(folder => ({
        id: folder?.id,
        name: folder?.name,
        description: folder?.description,
        documentBaseId: folder?.document_base_id,
        parentFolderId: folder?.parent_folder_id,
        ownerId: folder?.owner_id,
        ownerName: folder?.owner?.full_name,
        parentName: folder?.parent?.name,
        createdAt: folder?.created_at,
        updatedAt: folder?.updated_at
      }));
    } catch (error) {
      console.error('Error loading folders:', error);
      throw error;
    }
  },

  // Get all folders across all document bases
  async getAllFolders() {
    try {
      const { data, error } = await supabase?.from('folders')?.select(`
          *,
          owner:user_profiles!owner_id(full_name),
          parent:folders!parent_folder_id(name),
          document_base:document_bases!document_base_id(name)
        `)?.order('name');

      if (error) throw error;

      return data?.map(folder => ({
        id: folder?.id,
        name: folder?.name,
        description: folder?.description,
        documentBaseId: folder?.document_base_id,
        documentBaseName: folder?.document_base?.name,
        parentFolderId: folder?.parent_folder_id,
        ownerId: folder?.owner_id,
        ownerName: folder?.owner?.full_name,
        parentName: folder?.parent?.name,
        createdAt: folder?.created_at,
        updatedAt: folder?.updated_at,
        type: 'folder'
      }));
    } catch (error) {
      console.error('Error loading all folders:', error);
      throw error;
    }
  },

  async createFolder(name, description, documentBaseId, parentFolderId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('folders')?.insert({
          name: name?.trim(),
          description: description?.trim() || null,
          document_base_id: documentBaseId,
          parent_folder_id: parentFolderId,
          owner_id: user?.id
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        documentBaseId: data?.document_base_id,
        parentFolderId: data?.parent_folder_id,
        ownerId: data?.owner_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  },

  // Documents
  async getDocuments(documentBaseId, folderId = null) {
    try {
      let query = supabase?.from('documents')?.select(`
          *,
          owner:user_profiles!owner_id(full_name),
          folder:folders!folder_id(name)
        `)?.eq('document_base_id', documentBaseId)?.eq('status', 'active');

      if (folderId) {
        query = query?.eq('folder_id', folderId);
      } else {
        query = query?.is('folder_id', null);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(doc => ({
        id: doc?.id,
        name: doc?.name,
        description: doc?.description,
        filePath: doc?.file_path,
        fileSize: doc?.file_size,
        size: doc?.file_size,
        mimeType: doc?.mime_type,
        type: this.getFileTypeFromMime(doc?.mime_type),
        documentBaseId: doc?.document_base_id,
        folderId: doc?.folder_id,
        folderName: doc?.folder?.name,
        ownerId: doc?.owner_id,
        ownerName: doc?.owner?.full_name,
        status: doc?.status,
        version: doc?.version,
        createdAt: doc?.created_at,
        updatedAt: doc?.updated_at,
        uploadDate: doc?.created_at,
        // CRITICAL FIX: Include storage metadata for proper document opening
        storageType: doc?.storage_type || 'supabase',
        storage_type: doc?.storage_type || 'supabase', // Keep snake_case for compatibility
        sharePointItemId: doc?.sharepoint_item_id || null,
        sharepoint_item_id: doc?.sharepoint_item_id || null, // Keep snake_case for compatibility
        webUrl: doc?.web_url || null,
        web_url: doc?.web_url || null // Keep snake_case for compatibility
      }));
    } catch (error) {
      console.error('Error loading documents:', error);
      throw error;
    }
  },

  // Get single document by ID
  async getDocumentById(documentId) {
    try {
      const { data, error } = await supabase?.from('documents')?.select(`
          *,
          owner:user_profiles!owner_id(full_name, email),
          folder:folders!folder_id(name),
          document_base:document_bases!document_base_id(name)
        `)?.eq('id', documentId)?.eq('status', 'active')?.single();

      if (error) throw error;
      if (!data) throw new Error('Document not found');

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        filePath: data?.file_path,
        fileSize: data?.file_size,
        size: data?.file_size,
        mimeType: data?.mime_type,
        type: this.getFileTypeFromMime(data?.mime_type),
        documentBaseId: data?.document_base_id,
        documentBaseName: data?.document_base?.name,
        folderId: data?.folder_id,
        folderName: data?.folder?.name,
        ownerId: data?.owner_id,
        ownerName: data?.owner?.full_name,
        ownerEmail: data?.owner?.email,
        status: data?.status,
        version: data?.version,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        uploadDate: data?.created_at,
        // CRITICAL FIX: Include storage metadata for proper routing
        storageType: data?.storage_type || 'supabase',
        storage_type: data?.storage_type || 'supabase', // Keep snake_case for compatibility
        sharePointItemId: data?.sharepoint_item_id || null,
        sharepoint_item_id: data?.sharepoint_item_id || null, // Keep snake_case for compatibility
        webUrl: data?.web_url || null,
        web_url: data?.web_url || null // Keep snake_case for compatibility
      };
    } catch (error) {
      console.error('Error loading document:', error);
      throw error;
    }
  },

  // Helper method to get clean file type from MIME type
  getFileTypeFromMime(mimeType) {
    if (!mimeType) return 'FILE';
    
    // Handle common MIME types
    const mimeTypeMap = {
      'application/pdf': 'PDF',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'text/plain': 'TXT',
      'text/csv': 'CSV',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'application/zip': 'ZIP',
      'application/x-rar-compressed': 'RAR'
    };

    // Return mapped type or extract from MIME type
    if (mimeTypeMap?.[mimeType]) {
      return mimeTypeMap?.[mimeType];
    }

    // Extract file type from complex MIME types
    const parts = mimeType?.split('/');
    if (parts?.length > 1) {
      const subtype = parts?.[1];
      // Handle Microsoft Office formats
      if (subtype?.includes('wordprocessingml')) return 'DOCX';
      if (subtype?.includes('spreadsheetml')) return 'XLSX';
      if (subtype?.includes('presentationml')) return 'PPTX';
      
      // Return the subtype in uppercase, cleaned up
      return subtype?.split('.')?.pop()?.toUpperCase() || 'FILE';
    }

    return 'FILE';
  },

  async uploadDocument(file, documentBaseId, folderId = null, description = '') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // CRITICAL FIX: Always get current storage strategy from localStorage
      const currentStrategy = this.getStorageStrategy();
      console.log('[UPLOAD] Current storage strategy:', currentStrategy);

      // Validate folder exists if folderId is provided
      // Only validate if folderId is a valid UUID and not the same as documentBaseId
      if (folderId && folderId !== documentBaseId && folderId !== null && folderId !== undefined) {
        try {
          const { data: folderExists, error: folderError } = await supabase?.from('folders')
            ?.select('id')
            ?.eq('id', folderId)
            ?.eq('document_base_id', documentBaseId)
            ?.single();
          
          if (folderError || !folderExists) {
            console.warn('[UPLOAD] Folder not found or invalid, uploading to root folder instead:', folderError?.message || 'Folder does not exist');
            folderId = null;
          }
        } catch (folderValidationError) {
          console.warn('[UPLOAD] Error validating folder, uploading to root folder instead:', folderValidationError);
          folderId = null;
        }
      } else if (folderId === documentBaseId) {
        // If folderId is the same as documentBaseId, it's likely a mistake - treat as null
        console.warn('[UPLOAD] folderId matches documentBaseId, treating as root folder');
        folderId = null;
      }

      let filePath, fileUrl, sharePointItemId;

      // Route to appropriate storage based on strategy
      if (currentStrategy === 'sharepoint') {
        console.log('[UPLOAD] Using SharePoint storage');
        
        // Build folder path for SharePoint
        let sharePointFolderPath = 'ZentriDMS';
        if (folderId) {
          // Get folder structure to build path
          const { data: folder } = await supabase?.from('folders')
            ?.select('name, parent_folder_id')
            ?.eq('id', folderId)
            ?.single();
          
          if (folder) {
            sharePointFolderPath += `/${folder?.name}`;
          }
        }

        // Upload to SharePoint
        const uploadResult = await sharepointService?.uploadDocument(
          file,
          file?.name,
          sharePointFolderPath
        );

        filePath = uploadResult?.webUrl; // Store SharePoint web URL
        fileUrl = uploadResult?.webUrl;
        sharePointItemId = uploadResult?.id;
        
        console.log('[UPLOAD] SharePoint upload successful:', uploadResult?.name);
      } else {
        console.log('[UPLOAD] Using Supabase storage');
        
        // Original Supabase upload logic
        const fileName = `${user?.id}/${Date.now()}-${file?.name}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('documents')
          ?.upload(fileName, file);

        if (uploadError) throw uploadError;

        filePath = uploadData?.path;
        fileUrl = null; // Will be generated on demand for Supabase
      }

      // Create document record in database with storage metadata
      const { data, error } = await supabase?.from('documents')?.insert({
          name: file?.name,
          description: description?.trim() || null,
          file_path: filePath,
          file_size: file?.size,
          mime_type: file?.type,
          document_base_id: documentBaseId,
          folder_id: folderId,
          owner_id: user?.id,
          storage_type: currentStrategy,
          sharepoint_item_id: sharePointItemId || null,
          web_url: fileUrl || null
        })?.select()?.single();

      if (error) throw error;

      console.log('[UPLOAD] Document record created in database:', {
        id: data?.id,
        name: data?.name,
        storage_type: data?.storage_type,
        sharepoint_item_id: data?.sharepoint_item_id
      });

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        filePath: data?.file_path,
        fileSize: data?.file_size,
        mimeType: data?.mime_type,
        documentBaseId: data?.document_base_id,
        folderId: data?.folder_id,
        ownerId: data?.owner_id,
        status: data?.status,
        version: data?.version,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        storageType: data?.storage_type,
        sharePointItemId: data?.sharepoint_item_id,
        webUrl: data?.web_url
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  async getDocumentUrl(filePath, storageType = 'supabase', sharePointItemId = null) {
    try {
      if (storageType === 'sharepoint') {
        // For SharePoint, filePath is already the webUrl
        return filePath;
      } else {
        // For Supabase, generate signed URL
        const { data, error } = await supabase?.storage
          ?.from('documents')
          ?.createSignedUrl(filePath, 3600);

        if (error) throw error;
        return data?.signedUrl;
      }
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  },

  // UPDATED METHOD: Get document content for editing with proper storage routing
  async getDocumentContent(documentId, filePath, mimeType, storageType = 'supabase', sharePointItemId = null) {
    try {
      console.log('[GET_CONTENT] Fetching document content:', {
        documentId,
        filePath,
        storageType,
        sharePointItemId
      });

      let content;

      // Route to appropriate storage based on document metadata
      if (storageType === 'sharepoint' && sharePointItemId) {
        console.log('[GET_CONTENT] Downloading from SharePoint:', sharePointItemId);
        
        // For SharePoint documents, download via SharePoint service
        // Try to get siteId and driveId from localStorage (set by SharePointContext)
        try {
          let siteId = null;
          let driveId = null;
          
          // Try to get from localStorage first (set by SharePointContext)
          try {
            const siteStr = localStorage.getItem('zentri_sharepoint_site');
            const driveStr = localStorage.getItem('zentri_sharepoint_drive');
            
            if (siteStr) {
              const site = JSON.parse(siteStr);
              siteId = site?.id;
              console.log('[GET_CONTENT] Found siteId in localStorage:', siteId);
              
              // Ensure sharepointService has siteId set
              if (siteId) sharepointService?.setSiteId(siteId);
            }
            
            if (driveStr) {
              const drive = JSON.parse(driveStr);
              driveId = drive?.id;
              console.log('[GET_CONTENT] Found driveId in localStorage:', driveId);
              
              // Ensure sharepointService has driveId set
              if (driveId) sharepointService?.setDriveId(driveId);
            }
            
            if (!siteId) {
              console.warn('[GET_CONTENT] SharePoint site not found in localStorage. User may need to configure SharePoint.');
            }
          } catch (localStorageError) {
            console.warn('[GET_CONTENT] Could not read from localStorage:', localStorageError);
          }
          
          // Download with siteId and driveId (downloadDocument will handle fallback to default drive if driveId is missing)
          content = await sharepointService?.downloadDocument(sharePointItemId, siteId, driveId);
          console.log('[GET_CONTENT] SharePoint download successful');
        } catch (sharePointError) {
          console.error('[GET_CONTENT] SharePoint download failed:', sharePointError);
          
          // Provide more helpful error message
          let errorMessage = sharePointError?.message || 'Unknown error';
          if (errorMessage?.includes('Site ID') && errorMessage?.includes('must be set')) {
            errorMessage = 'SharePoint is not configured. Please configure SharePoint in System Settings → Integrations before viewing SharePoint documents.';
          }
          
          throw new Error(`Failed to download from SharePoint: ${errorMessage}. Please verify your SharePoint connection and permissions.`);
        }
      } else {
        console.log('[GET_CONTENT] Downloading from Supabase storage:', filePath);
        
        // For Supabase documents, download from Supabase storage
        try {
          const { data, error } = await supabase?.storage?.from('documents')?.download(filePath);
          
          if (error) {
            console.error('[GET_CONTENT] Supabase storage error:', error);
            throw new Error(`Supabase storage error: ${error?.message}`);
          }
          
          if (!data) {
            throw new Error('No data returned from Supabase storage');
          }
          
          content = data;
          console.log('[GET_CONTENT] Supabase download successful');
        } catch (supabaseError) {
          console.error('[GET_CONTENT] Supabase download failed:', supabaseError);
          throw new Error(`Failed to download from Supabase: ${supabaseError?.message}. Please verify the document exists.`);
        }
      }

      // Handle different document types
      if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
          mimeType === 'application/msword') {
        // For Word documents, return blob for mammoth processing
        console.log('[GET_CONTENT] Returning Word document blob for processing');
        return content;
      } else if (mimeType === 'text/plain') {
        // For plain text files
        console.log('[GET_CONTENT] Converting text document to string');
        return await content?.text();
      } else if (mimeType === 'application/pdf') {
        // For PDF files, return the blob for PDF viewer
        console.log('[GET_CONTENT] Returning PDF blob for viewer');
        return content;
      } else {
        // For other file types, try to get text content
        console.log('[GET_CONTENT] Attempting to convert unknown type to text');
        try {
          return await content?.text();
        } catch (textError) {
          console.warn('[GET_CONTENT] Could not convert to text, returning blob:', textError);
          return content;
        }
      }
    } catch (error) {
      console.error('[GET_CONTENT] Error getting document content:', error);
      
      // Provide more specific error messages
      if (error?.message?.includes('SharePoint')) {
        throw new Error(`SharePoint Error: ${error?.message}. Check your Microsoft Graph integration settings.`);
      } else if (error?.message?.includes('Supabase')) {
        throw new Error(`Storage Error: ${error?.message}. The document may have been moved or deleted.`);
      } else if (error?.message?.includes('Not authenticated')) {
        throw new Error('Authentication Error: Please sign in again to access document content.');
      } else {
        throw new Error(`Document retrieval error: ${error?.message || 'Unknown error occurred'}`);
      }
    }
  },

  async deleteDocument(id, filePath, storageType = 'supabase', sharePointItemId = null) {
    try {
      // Delete from appropriate storage
      if (storageType === 'sharepoint' && sharePointItemId) {
        console.log('[DELETE] Deleting from SharePoint:', sharePointItemId);
        try {
          await sharepointService?.deleteDocument(sharePointItemId);
        } catch (sharePointError) {
          console.error('[DELETE] SharePoint deletion failed:', sharePointError);
          
          // Check if the error is due to document not found in SharePoint (404)
          if (sharePointError?.message?.includes('404') || 
              sharePointError?.message?.includes('not found') ||
              sharePointError?.message?.toLowerCase()?.includes('itemnotfound')) {
            
            // Document doesn't exist in SharePoint - this is an orphaned document
            console.log('[DELETE] Document not found in SharePoint (orphaned), removing from database silently');
            
            // Get document details for audit log
            const { data: doc } = await supabase
              ?.from('documents')
              ?.select('name')
              ?.eq('id', id)
              ?.single();
            
            // Log the orphaned document removal (no error thrown)
            await auditLogService?.logOrphanedDocumentRemoval(
              id,
              doc?.name || 'Unknown Document',
              sharePointItemId,
              'Document not found in SharePoint - automatically removed from database'
            );
            
            // Continue to database deletion without throwing error
            console.log('[DELETE] Proceeding with database cleanup for orphaned document');
          } else if (sharePointError?.message?.includes('SharePoint Integration Not') ||
                     sharePointError?.message?.includes('Edge Function not found') ||
                     sharePointError?.message?.includes('Not Deployed')) {
            // SharePoint integration not available - provide helpful error message
            throw new Error(
              '⚠️ Cannot Delete SharePoint Document\n\n' +
              'This document is stored in SharePoint, but the SharePoint integration is not currently available.\n\n'+ 'Possible reasons:\n'+ '• Microsoft Graph Edge Function is not deployed\n'+ '• SharePoint credentials are not configured\n'+ '• Network connectivity issues\n\n'+ '🔧 To resolve:\n'+ '1. Contact your system administrator to deploy the Microsoft Graph Edge Function\n'+ '2. Verify SharePoint integration settings in System Settings\n'+ '3. Check SUPABASE_EDGE_FUNCTIONS_SETUP.md for deployment instructions\n\n'+ '💡 Note: You can still delete documents stored in Supabase storage.'
            );
          } else {
            // Other SharePoint errors - re-throw with context
            throw new Error(`SharePoint deletion error: ${sharePointError?.message}`);
          }
        }
      } else {
        console.log('[DELETE] Deleting from Supabase storage:', filePath);
        const { error: storageError } = await supabase?.storage?.from('documents')?.remove([filePath]);
        if (storageError) {
          console.error('[DELETE] Supabase storage deletion failed:', storageError);
          throw new Error(`Failed to delete file from storage: ${storageError?.message}`);
        }
      }

      // Delete from database
      const { error } = await supabase?.from('documents')?.delete()?.eq('id', id);

      if (error) throw error;
      
      console.log('[DELETE] Document successfully deleted from database');
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // Add document rename method
  async renameDocumentWithValidation(id, newName) {
    try {
      if (!newName?.trim()) {
        throw new Error('Name cannot be empty');
      }

      // Get document details first
      const { data: doc, error: fetchError } = await supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (fetchError || !doc) {
        throw new Error('Document not found');
      }

      // Check permissions
      const permission = await this.canManageItem('document', id, doc?.owner_id);
      if (!permission?.canManage) {
        throw new Error(permission?.reason || 'Access denied');
      }

      // Update the name
      const { data, error } = await supabase
        ?.from('documents')
        ?.update({ name: newName?.trim() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        filePath: data?.file_path,
        fileSize: data?.file_size,
        mimeType: data?.mime_type,
        documentBaseId: data?.document_base_id,
        folderId: data?.folder_id,
        ownerId: data?.owner_id,
        status: data?.status,
        version: data?.version,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error renaming document:', error);
      throw error;
    }
  },

  // Enhanced document delete with validation - update to handle storage type
  async deleteDocumentWithValidation(id, filePath) {
    try {
      console.log('[DELETE] Starting document deletion for ID:', id);
      
      // Get document details first (includes storage metadata)
      const { data: doc, error: fetchError } = await supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        console.error('[DELETE] Error fetching document:', fetchError);
        throw new Error(`Failed to fetch document: ${fetchError?.message}`);
      }
      
      if (!doc) {
        console.error('[DELETE] Document not found');
        throw new Error('Document not found');
      }

      console.log('[DELETE] Document found:', doc?.name, 'Storage:', doc?.storage_type || 'supabase');

      // Check permissions
      const permission = await this.canManageItem('document', id, doc?.owner_id);
      if (!permission?.canManage) {
        console.error('[DELETE] Permission denied:', permission?.reason);
        throw new Error(permission?.reason || 'Access denied');
      }

      console.log('[DELETE] Permission granted, proceeding with deletion');

      // Delete from appropriate storage based on document metadata
      await this.deleteDocument(
        id, 
        filePath || doc?.file_path,
        doc?.storage_type || 'supabase',
        doc?.sharepoint_item_id
      );

      console.log('[DELETE] Document deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('[DELETE] Error in deleteDocumentWithValidation:', error);
      throw error;
    }
  },

  /**
   * Cleanup orphaned SharePoint documents
   * Identifies documents in Supabase that no longer exist in SharePoint and removes them
   * @param {string} documentBaseId - Optional: limit cleanup to specific document base
   * @returns {Promise<Object>} Cleanup results with counts
   */
  async cleanupOrphanedSharePointDocuments(documentBaseId = null) {
    try {
      console.log('[CLEANUP] Starting orphaned SharePoint document cleanup');
      
      // Get all SharePoint documents from Supabase
      let query = supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('storage_type', 'sharepoint')
        ?.not('sharepoint_item_id', 'is', null);
      
      if (documentBaseId) {
        query = query?.eq('document_base_id', documentBaseId);
      }
      
      const { data: sharePointDocs, error } = await query;
      
      if (error) {
        console.error('[CLEANUP] Error fetching SharePoint documents:', error);
        throw error;
      }
      
      console.log(`[CLEANUP] Found ${sharePointDocs?.length || 0} SharePoint documents to verify`);
      
      if (!sharePointDocs || sharePointDocs?.length === 0) {
        return {
          total: 0,
          verified: 0,
          removed: 0,
          errors: []
        };
      }
      
      const results = {
        total: sharePointDocs?.length,
        verified: 0,
        removed: 0,
        errors: []
      };
      
      // Check each document in SharePoint
      for (const doc of sharePointDocs) {
        try {
          console.log(`[CLEANUP] Verifying document: ${doc?.name} (${doc?.sharepoint_item_id})`);
          
          // Try to get document metadata from SharePoint
          await microsoftGraphService?.callGraphAPI(
            `/sites/${sharepointService?.siteId}/drives/${sharepointService?.driveId}/items/${doc?.sharepoint_item_id}`
          );
          
          // If we got here, document exists in SharePoint
          results.verified++;
          console.log(`[CLEANUP] Document verified in SharePoint: ${doc?.name}`);
          
        } catch (verifyError) {
          // Check if error is 404 (document not found in SharePoint)
          if (verifyError?.message?.includes('404') || 
              verifyError?.message?.includes('not found') ||
              verifyError?.message?.toLowerCase()?.includes('itemnotfound')) {
            
            console.log(`[CLEANUP] Document not found in SharePoint, removing: ${doc?.name}`);
            
            try {
              // Delete from Supabase database (silently)
              const { error: deleteError } = await supabase
                ?.from('documents')
                ?.delete()
                ?.eq('id', doc?.id);
              
              if (deleteError) {
                console.error(`[CLEANUP] Error deleting document ${doc?.name}:`, deleteError);
                results?.errors?.push({
                  documentId: doc?.id,
                  documentName: doc?.name,
                  error: deleteError?.message
                });
              } else {
                // Log the orphaned document removal
                await auditLogService?.logOrphanedDocumentRemoval(
                  doc?.id,
                  doc?.name,
                  doc?.sharepoint_item_id,
                  'Document not found in SharePoint during cleanup operation'
                );
                
                results.removed++;
                console.log(`[CLEANUP] Successfully removed orphaned document: ${doc?.name}`);
              }
            } catch (cleanupError) {
              console.error(`[CLEANUP] Error during cleanup for ${doc?.name}:`, cleanupError);
              results?.errors?.push({
                documentId: doc?.id,
                documentName: doc?.name,
                error: cleanupError?.message
              });
            }
          } else {
            // Other errors (network, permissions, etc.) - log but don't remove
            console.warn(`[CLEANUP] Could not verify document ${doc?.name}, skipping:`, verifyError?.message);
            results?.errors?.push({
              documentId: doc?.id,
              documentName: doc?.name,
              error: `Verification failed: ${verifyError?.message}`
            });
          }
        }
      }
      
      console.log('[CLEANUP] Orphaned document cleanup completed:', results);
      return results;
      
    } catch (error) {
      console.error('[CLEANUP] Error in cleanup operation:', error);
      throw error;
    }
  },

  // Add role-based validation method
  async canManageItem(itemType, itemId, ownerId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { canManage: false, reason: 'Not authenticated' };

      // Get user profile to check role
      const { data: profile } = await supabase?.from('user_profiles')
        ?.select('role')
        ?.eq('id', user?.id)
        ?.single();

      if (!profile) return { canManage: false, reason: 'User profile not found' };

      // Admin can manage everything
      if (profile?.role === 'admin') {
        return { canManage: true, reason: 'Admin privileges' };
      }

      // Manager can manage their own items and items in their department
      if (profile?.role === 'manager') {
        if (ownerId && ownerId === user?.id) {
          return { canManage: true, reason: 'Owner privileges' };
        }
        // Add more manager-specific logic here if needed
        return { canManage: true, reason: 'Manager privileges' };
      }

      // Regular users can only manage their own items
      if (ownerId && ownerId === user?.id) {
        return { canManage: true, reason: 'Owner privileges' };
      }

      return { canManage: false, reason: 'Insufficient permissions' };
    } catch (error) {
      console.error('Error checking permissions:', error);
      return { canManage: false, reason: 'Permission check failed' };
    }
  },

  // Search
  async searchDocuments(query, documentBaseId = null) {
    try {
      let dbQuery = supabase?.from('documents')?.select(`
          *,
          owner:user_profiles!owner_id(full_name),
          document_base:document_bases!document_base_id(name)
        `)?.eq('status', 'active')?.or(`name.ilike.%${query}%,description.ilike.%${query}%`);

      if (documentBaseId) {
        dbQuery = dbQuery?.eq('document_base_id', documentBaseId);
      }

      const { data, error } = await dbQuery?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(doc => ({
        id: doc?.id,
        name: doc?.name,
        description: doc?.description,
        filePath: doc?.file_path,
        fileSize: doc?.file_size,
        mimeType: doc?.mime_type,
        documentBaseId: doc?.document_base_id,
        documentBaseName: doc?.document_base?.name,
        folderId: doc?.folder_id,
        ownerId: doc?.owner_id,
        ownerName: doc?.owner?.full_name,
        status: doc?.status,
        version: doc?.version,
        createdAt: doc?.created_at,
        updatedAt: doc?.updated_at
      }));
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  },

  // Add method to get all documents for document selector
  async getAllDocuments() {
    try {
      const { data: documents, error } = await supabase?.from('documents')?.select(`
          id,
          name,
          description,
          file_path,
          file_size,
          mime_type,
          created_at,
          updated_at,
          owner_id,
          document_base_id,
          folder_id,
          profiles:owner_id (
            full_name,
            email
          ),
          document_bases:document_base_id (
            name
          )
        `)?.order('updated_at', { ascending: false })?.limit(50);

      if (error) throw error;

      return documents?.map(doc => ({
        id: doc?.id,
        name: doc?.name,
        description: doc?.description,
        filePath: doc?.file_path,
        size: doc?.file_size,
        mimeType: doc?.mime_type,
        type: this.getFileTypeFromMime(doc?.mime_type),
        createdAt: doc?.created_at,
        updatedAt: doc?.updated_at,
        ownerId: doc?.owner_id,
        ownerName: doc?.profiles?.full_name || doc?.profiles?.email,
        documentBaseId: doc?.document_base_id,
        documentBaseName: doc?.document_bases?.name,
        folderId: doc?.folder_id
      })) || [];
    } catch (error) {
      console.error('Error fetching all documents:', error);
      throw new Error('Failed to fetch documents');
    }
  }
};